import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UsersIcon,
  CreditCardIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  PhoneIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import adminAPI from '../utils/api';
import toast from 'react-hot-toast';
import ClientServiceEditModal from '../components/ClientServiceEditModal';

const ServiceClients = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedClientService, setSelectedClientService] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch service details and clients in parallel
      const [serviceData, clientsData] = await Promise.all([
        adminAPI.getServiceType(serviceId),
        adminAPI.getServiceClients(serviceId)
      ]);

      setService(serviceData);
      setClients(Array.isArray(clientsData) ? clientsData : (clientsData.results || []));
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (serviceId) {
      fetchData();
    }
  }, [serviceId]);

  // Handle edit client service
  const handleEditClientService = (clientService) => {
    setSelectedClientService(clientService);
    setShowEditModal(true);
  };

  // Handle successful edit
  const handleEditSuccess = () => {
    setShowEditModal(false);
    setSelectedClientService(null);
    fetchData(); // Refresh the data
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">Erreur: {error}</div>
        <button
          onClick={() => navigate('/admin/services')}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
        >
          Retour aux services
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/admin/services')}
          className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div className="flex items-center space-x-3">
          <CreditCardIcon className="h-8 w-8 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Clients utilisant: {service?.name}
            </h1>
            <p className="text-gray-600">
              {service?.type === 'pack' ? 'Pack' : 'Abonnement'} - {service?.session_count} séances
              {service?.type === 'abonnement' && ' / mois'}
            </p>
          </div>
        </div>
      </div>

      {/* Service Info */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600">{clients.length}</div>
            <div className="text-sm text-gray-600">Total clients</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {(() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                return clients.filter(clientService => {
                  const endDate = new Date(clientService.end_date);
                  endDate.setHours(0, 0, 0, 0);

                  const isExpired = endDate < today;
                  const hasNoSessions = service?.type === 'pack' ?
                    clientService.used_sessions >= clientService.total_sessions :
                    clientService.current_month_used >= service?.session_count;

                  const isTrulyActive = clientService.is_truly_active !== undefined ? clientService.is_truly_active :
                    (clientService.status === 'active' && !isExpired && !hasNoSessions);

                  return isTrulyActive;
                }).length;
              })()}
            </div>
            <div className="text-sm text-gray-600">Services actifs</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{service?.price || 0} MAD</div>
            <div className="text-sm text-gray-600">Prix du service</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{service?.session_count}</div>
            <div className="text-sm text-gray-600">
              Séances {service?.type === 'abonnement' ? '/ mois' : 'totales'}
            </div>
          </div>
        </div>
      </div>

      {/* Clients Table */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilisation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Période
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {clients.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <UsersIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-lg font-medium">Aucun client trouvé</p>
                    <p className="text-sm">Aucun client n'utilise actuellement ce service</p>
                  </td>
                </tr>
              ) : (
                clients.map((clientService) => (
                  <tr key={clientService.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-primary-600 font-medium text-sm">
                              {clientService.client?.full_name?.charAt(0) || 'C'}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {clientService.client?.full_name || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {clientService.client?.id?.slice(0, 8)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="flex items-center text-sm text-gray-900">
                          <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-400" />
                          {clientService.client?.email || 'N/A'}
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                          {clientService.client?.phone || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {service?.type === 'pack' ? (
                          <>
                            <div className="font-medium">
                              {clientService.used_sessions || 0} / {clientService.total_sessions || 0}
                            </div>
                            <div className="text-xs text-gray-500">séances utilisées</div>
                          </>
                        ) : (
                          <>
                            <div className="font-medium">
                              {clientService.current_month_used || 0} / {service?.session_count || 0}
                            </div>
                            <div className="text-xs text-gray-500">ce mois</div>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center">
                          <CalendarDaysIcon className="h-4 w-4 mr-1 text-gray-400" />
                          {clientService.start_date}
                        </div>
                        <div className="text-xs text-gray-500">
                          au {clientService.end_date}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const endDate = new Date(clientService.end_date);
                        endDate.setHours(0, 0, 0, 0);

                        const isExpired = endDate < today;
                        const hasNoSessions = service?.type === 'pack' ?
                          clientService.used_sessions >= clientService.total_sessions :
                          clientService.current_month_used >= service?.session_count;

                        const isTrulyActive = clientService.is_truly_active !== undefined ? clientService.is_truly_active :
                          (clientService.status === 'active' && !isExpired && !hasNoSessions);

                        let statusText = 'Actif';
                        let statusClass = 'bg-green-100 text-green-800';

                        if (!isTrulyActive) {
                          if (isExpired) {
                            statusText = 'Expiré';
                            statusClass = 'bg-red-100 text-red-800';
                          } else if (hasNoSessions) {
                            statusText = 'Épuisé';
                            statusClass = 'bg-orange-100 text-orange-800';
                          } else if (clientService.status !== 'active') {
                            statusText = 'Inactif';
                            statusClass = 'bg-gray-100 text-gray-800';
                          }
                        }

                        return (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusClass}`}>
                            {statusText}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditClientService(clientService)}
                        className="text-primary-600 hover:text-primary-900 p-1 rounded"
                        title="Modifier le service"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Client Service Modal */}
      {showEditModal && selectedClientService && (
        <ClientServiceEditModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleEditSuccess}
          clientService={selectedClientService}
        />
      )}
    </div>
  );
};

export default ServiceClients;
